const ACTIVE_STATUSES = [ "active", "pending-cancel" ];

/**
 * Derives subscription status information for a site.
 *
 * A site is considered active when at least one of its subscriptions is active or pending-cancel.
 * The ends-on date is the earliest expiry (end date, or next payment as fallback) among those subscriptions.
 *
 * Reads the lean subscriptions the sites endpoint nests on each site rather than looking ids up in
 * the account-wide subscriptions map, so the sites screens no longer depend on that whole collection
 * being loaded.
 *
 * @param {Object} site A viewable site, with `subscriptionDetails` as an array of lean subscriptions.
 *
 * @returns {{isActive: boolean, endsOn: ?Date}} Whether the site is active and when its subscription ends.
 */
export function getSiteSubscriptionInfo( site ) {
	const activeSubscriptions = ( site.subscriptionDetails || [] )
		.filter( subscription => subscription && ACTIVE_STATUSES.includes( subscription.status ) );

	if ( activeSubscriptions.length === 0 ) {
		return { isActive: false, endsOn: null };
	}

	const expiryTimes = activeSubscriptions
		.map( subscription => subscription.endDate || subscription.nextPayment )
		.filter( Boolean )
		.map( date => new Date( date ).getTime() );

	return {
		isActive: true,
		endsOn: expiryTimes.length > 0 ? new Date( Math.min( ...expiryTimes ) ) : null,
	};
}

/**
 * Formats a date as dd-mm-yyyy.
 *
 * @param {Date} date The date to format.
 *
 * @returns {string} The formatted date.
 */
export function formatDayMonthYear( date ) {
	const day = String( date.getDate() ).padStart( 2, "0" );
	const month = String( date.getMonth() + 1 ).padStart( 2, "0" );

	return `${ day }-${ month }-${ date.getFullYear() }`;
}
